// Pan/zoom/follow for a FarmWorld container. Follow-cam by default; a drag (>8 px) pans and
// pauses following; the wheel sets a zoom target the view eases to about the pointer (pinch
// zooms directly); the zoom is remembered across visits (farmZoomMemory); when following
// resumes after a pan the camera glides back slowly (farmView.followTau); a clean tap not
// over a HUD object reports the cell under it. Pointer coordinates are `pointer.worldX/worldY` (scene
// units under the retina camera), never canvas pixels.
import Phaser from 'phaser';
import { localToCell, inFarm } from './iso.mjs';
import {
  createView, screenToLocal, followView, panView, zoomAt, clampView, lerpView,
  wheelZoomTarget, stepZoom, easeFactor, followTau, FOLLOW, approachView,
} from './farmView.mjs';
import { rememberedZoom, rememberZoom } from './farmZoomMemory.mjs';

const DRAG_PX = 8;

export function attachFarmCameraControl(scene, world, { getTarget, onTap, onHover, viewport, dragMode = null, onDragStart, onDrag, onDragEnd }) {
  const zoom0 = rememberedZoom();
  const ctl = {
    view: createView(zoom0), follow: true, _press: null, _pinch: null,
    zoomTarget: zoom0, zoomFocus: null,
    recentering: false, recenterMs: 0,
  };
  const clampNow = (v) => clampView(v, world.extent, viewport);
  const overHud = (p) => scene.input.hitTestPointer(p).length > 0;   // world tiles are never interactive

  const screenToCell = (sx, sy) => {
    const l = screenToLocal(ctl.view, sx, sy);
    const cell = localToCell(l.x, l.y, world.size);
    return inFarm(cell, world.size) ? cell : null;
  };

  const onDown = (p) => {
    if (overHud(p)) return;
    // Drawing a fence: a press-drag draws instead of panning (FarmPlacer).
    if (dragMode?.()) { ctl._drag = true; onDragStart?.(screenToCell(p.worldX, p.worldY)); return; }
    ctl._press = { x: p.worldX, y: p.worldY, view: { ...ctl.view }, moved: false };
  };
  const onMove = (p) => {
    // Over a pop-up / HUD button (they don't all lock input): no field cell under the pointer, so the
    // crop hidden behind it can't pick the cursor (owner 2026-09-30).
    const hud = overHud(p);
    onHover?.(hud ? null : screenToCell(p.worldX, p.worldY), { x: p.worldX, y: p.worldY, overHud: hud });
    if (ctl._drag) { if (p.isDown) onDrag?.(screenToCell(p.worldX, p.worldY)); return; }
    if (!ctl._press || !p.isDown || ctl._pinch) return;
    const dx = p.worldX - ctl._press.x, dy = p.worldY - ctl._press.y;
    if (!ctl._press.moved && Math.hypot(dx, dy) < DRAG_PX) return;
    ctl._press.moved = true;
    ctl.follow = false;
    ctl.view = clampNow(panView(ctl._press.view, dx, dy));
  };
  const onUp = (p) => {
    if (ctl._drag) { ctl._drag = false; onDragEnd?.(screenToCell(p.worldX, p.worldY)); return; }
    const press = ctl._press;
    ctl._press = null;
    // A finger leaves no cursor behind. Touch only delivers pointermove while it is down,
    // so the hover diamond and its tooltip would sit on the last touched cell for ever.
    if (p.wasTouch) onHover?.(null, { x: p.worldX, y: p.worldY });
    if (!press || press.moved || ctl._pinch) return;
    const cell = screenToCell(p.worldX, p.worldY);
    onTap?.(cell, { x: p.worldX, y: p.worldY, wasTouch: !!p.wasTouch });
  };
  const onWheel = (p, _objs, _dx, dy) => {
    ctl.zoomTarget = wheelZoomTarget(ctl.zoomTarget, dy);
    ctl.zoomFocus = { x: p.worldX, y: p.worldY };
    rememberZoom(ctl.zoomTarget);
  };

  scene.input.addPointer(1);   // two touch points for pinch
  scene.input.on('pointerdown', onDown);
  scene.input.on('pointermove', onMove);
  scene.input.on('pointerup', onUp);
  scene.input.on('pointerupoutside', onUp);
  scene.input.on('wheel', onWheel);

  const pinch = () => {
    const p1 = scene.input.pointer1, p2 = scene.input.pointer2;
    if (!(p1?.isDown && p2?.isDown)) { ctl._pinch = null; return; }
    const d = Phaser.Math.Distance.Between(p1.worldX, p1.worldY, p2.worldX, p2.worldY);
    const mid = { x: (p1.worldX + p2.worldX) / 2, y: (p1.worldY + p2.worldY) / 2 };
    if (ctl._pinch) {
      ctl.follow = false;
      ctl._press = null;
      ctl.view = clampNow(zoomAt(ctl.view, mid, ctl.view.zoom * (d / ctl._pinch)));
      ctl.zoomTarget = ctl.view.zoom;   // a pinch is direct manipulation: no easing after it
      rememberZoom(ctl.zoomTarget);
    }
    ctl._pinch = d;
  };

  const update = (dt = 16) => {
    pinch();
    if (Math.abs(ctl.zoomTarget - ctl.view.zoom) > 1e-6) {
      const focus = ctl.zoomFocus ?? { x: viewport.w / 2, y: viewport.h / 2 };
      ctl.view = clampNow(stepZoom(ctl.view, ctl.zoomTarget, focus, dt));
    }
    if (ctl.follow) {
      const t = getTarget();
      let tau = FOLLOW.tauMs;
      if (ctl.recentering) {
        ctl.recenterMs += dt;
        tau = followTau(ctl.recenterMs);
        if (ctl.recenterMs >= FOLLOW.recenterRampMs) ctl.recentering = false;
      }
      if (t) ctl.view = approachView(ctl.view, clampNow(followView(ctl.view, t, viewport)), easeFactor(dt, tau));
    }
    world.container.setPosition(ctl.view.x, ctl.view.y).setScale(ctl.view.zoom);
  };

  const destroy = () => {
    scene.input.off('pointerdown', onDown);
    scene.input.off('pointermove', onMove);
    scene.input.off('pointerup', onUp);
    scene.input.off('pointerupoutside', onUp);
    scene.input.off('wheel', onWheel);
  };
  scene.events.once('shutdown', destroy);

  return {
    get view() { return ctl.view; },
    get follow() { return ctl.follow; },
    setFollow(on) {
      // Coming back from a manual pan: glide home instead of snapping.
      if (on && !ctl.follow) { ctl.recentering = true; ctl.recenterMs = 0; }
      ctl.follow = on;
    },
    snapToTarget() { const t = getTarget(); if (t) ctl.view = clampNow(followView(ctl.view, t, viewport)); },
    update, screenToCell, destroy,
  };
}
